import React, { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { t } from '@lingui/macro';

import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import Modal from '@rover/kibble/official-patterns/Modal';
import ClickableInput from '@rover/kibble/patterns/ClickableInput';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import type { ServiceTypeOption, ServiceTypeSlug } from '@rover/types/src/ServiceType';

import { ServiceList } from './ServiceList';

const ServiceListLazy = React.lazy(() =>
  import('./ServiceList').then((module) => ({ default: module.ServiceList }))
);

type Props = {
  inputFontSize?: string;
  label: string;
  serviceType: ServiceTypeSlug;
  serviceTypeOptions: ServiceTypeOption[];
  onChange: (value: ServiceTypeSlug) => void;
  onOpen?: () => void;
};

export const ServiceSelectorV2: FC<Props> = ({
  serviceType,
  serviceTypeOptions,
  onChange,
  label,
  inputFontSize,
  onOpen = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMatchMedia(MQ.MOBILE.getBreakpointString());
  const [isOpen, setIsOpen] = useState(false);

  const selectedService = useMemo(() => {
    return serviceTypeOptions.find(({ value }) => value === serviceType) || serviceTypeOptions[0];
  }, [serviceType, serviceTypeOptions]);

  const { icon: Icon } = selectedService;

  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleOpen = useCallback(() => {
    onOpen();
    setIsOpen(true);
  }, [onOpen]);

  const inputValue = useMemo(() => {
    return `${selectedService.title} • ${selectedService.subtitle}`;
  }, [selectedService]);

  return (
    <>
      <ClickableInput
        onClick={handleOpen}
        inputLabel={label}
        {...(inputFontSize ? { inputFontSize } : {})}
      >
        <Flex alignItems="center">
          <Flex alignItems="center" marginRight="2x" flex="0 0 auto">
            {Icon && <Icon fill={theme.colors.brand.active_brand} width="24px" height="24px" />}
          </Flex>
          <Text
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {inputValue}
          </Text>
        </Flex>
      </ClickableInput>
      {isMobile ? (
        <BottomSheet onRequestClose={handleClose} isOpen={isOpen}>
          <Heading fontWeight="semibold" textAlign="center">
            {t`Which service do you need?`}
          </Heading>
          <Suspense fallback={null}>
            <ServiceListLazy
              serviceTypeOptions={serviceTypeOptions}
              onChange={(serviceTypeSlug: ServiceTypeSlug) => {
                onChange(serviceTypeSlug);
                handleClose();
              }}
              serviceType={serviceType}
            />
          </Suspense>
        </BottomSheet>
      ) : (
        <Modal
          onRequestClose={handleClose}
          isOpen={isOpen}
          headerText={t`Select a service`}
          showCloseIcon
        >
          {/* there is no capability right now to make the modal expand to the full width of the screen,
            so we:
             - take the max width of the modal (600px)
             - limit it to the max viewport width (100vw)
             - and subtract the padding (24px on each side)
          */}
          <Box width={`calc(min(600px, 100vw) - ${24 * 2}px)`} paddingBottom="6x">
            <ServiceList
              serviceTypeOptions={serviceTypeOptions}
              onChange={(serviceTypeSlug: ServiceTypeSlug) => {
                onChange(serviceTypeSlug);
                handleClose();
              }}
              serviceType={serviceType}
            />
          </Box>
        </Modal>
      )}
    </>
  );
};
